/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMedia, useWindowDimensions, View } from 'tamagui'
import { Slider } from '@components/Slider'
import { useMemo, useState, useCallback } from 'react'
import { ProductCard } from '@components/Home/MyProducts/ProductCard'
import { CARD_DIMENSIONS } from '@constants/productCardDimension'
import { useMediaSize } from '@hooks/useMediaSize'
import { useGetGeneralConfig } from '@hooks/useMembersConfig'
import { DefaultSectionTitle } from '@components/Home/DefaultSectionTitle'
import { EnrollmentContentsModal } from '@components/Home/EnrollmentContentsModal'
import { LoadMoreSentinel } from '@components/LoadMoreSentinel'
import { useGuardedLoadMore } from '@hooks/useGuardedLoadMore'
import { ContinueCard } from '@components/Home/Continue/ContinueCard'
import { useToastController } from '@tamagui/toast'

interface IPathProductsSectionProps {
  enrollmentId: string
  newEnrollment: any
  onLoadMore: () => Promise<void>
  hasMore?: boolean
}

export function PathProductsSection({
  enrollmentId,
  newEnrollment,
  onLoadMore,
  hasMore = true,
}: IPathProductsSectionProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const sizes = useMediaSize()
  const media = useMedia()
  const toast = useToastController()
  const { width: windowWidth } = useWindowDimensions()
  const { data: generalConfig } = useGetGeneralConfig()
  const isHorizontal = true
  const showProductsName = generalConfig?.showProductName ?? true
  const containerPadding = media.gtSm ? 48 : 16
  const spaceBetweenCards = media.gtSm ? 20 : 16

  const shouldDisplaySliderControls = useMemo(() => {
    return media.gtMd && isHovered
  }, [media.gtMd, isHovered])

  const slidesPerView = useMemo(() => {
    const orientation = isHorizontal ? 'horizontal' : 'vertical'
    const cardWidth =
      CARD_DIMENSIONS[orientation][sizes] || CARD_DIMENSIONS[orientation].xs
    return (
      (windowWidth - containerPadding) / (cardWidth.width + spaceBetweenCards)
    )
  }, [isHorizontal, sizes, windowWidth, containerPadding, spaceBetweenCards])

  // Offset da próxima página: conta os cards crus (o backend pagina sobre eles),
  // não os filtrados por course nulo.
  const loadedCardsCount =
    newEnrollment.progressCard.courseProgressCards?.length ?? 0

  const validProgressCards = useMemo(() => {
    const cards = newEnrollment.progressCard.courseProgressCards
    if (!cards) return null
    return cards.filter((product: any) => product?.course?.id != null)
  }, [newEnrollment.progressCard.courseProgressCards])

  const shouldDisplaySeeAllButton = useMemo(() => {
    return !!validProgressCards && validProgressCards.length > slidesPerView
  }, [validProgressCards, slidesPerView])

  const handleTitleButtonPress = () => {
    setOpenModal(true)
  }

  const handleLoadMoreError = useCallback(() => {
    toast.show('Erro ao carregar conteúdos', {
      message: 'Recarregue a página ou tente novamente mais tarde.',
      type: 'error',
    })
  }, [toast])

  const { loadMore: handleLoadMoreContents, isLoadingMore } =
    useGuardedLoadMore({
      offset: loadedCardsCount,
      hasMore,
      onLoadMore,
      onError: handleLoadMoreError,
    })

  const renderModalCards = useMemo(() => {
    if (!validProgressCards) return []

    const cards = validProgressCards.map((product: any) => (
      <ContinueCard
        cardId={product.course.id.toString()}
        showProductName={showProductsName}
        key={product.course.id.toString()}
        shouldExtendCard={false}
        productTitle={product.course.name}
        isModalVersion={true}
        customThumbnail={product.course.imageUrl}
        displayPlayIcon={false}
        displayBuyTag={false}
        progressCard={product}
        usedAsProduct={true}
        course={product.course}
        firstLesson={
          product.course?.courseModules?.nodes[0]?.courseContents.nodes[0]
            ?.lesson ?? undefined
        }
        closeModal={() => setOpenModal(false)}
        enrollmentId={enrollmentId}
      />
    ))

    // Sentinela depois dos cards, re-armada a cada página (resetKey). Antes ela
    // envolvia o último card e dependia da transição fora->dentro do InView: com
    // o usuário parado no fim da lista a próxima página nunca era pedida e
    // produtos do combo ficavam invisíveis (HET-3487).
    if (hasMore) {
      cards.push(
        <LoadMoreSentinel
          key={`load-more-${loadedCardsCount}`}
          onVisible={handleLoadMoreContents}
          resetKey={loadedCardsCount}
          testID="products-modal-load-more"
        />,
      )
    }

    return cards
  }, [
    validProgressCards,
    showProductsName,
    enrollmentId,
    handleLoadMoreContents,
    hasMore,
    loadedCardsCount,
  ])

  if (!validProgressCards || validProgressCards.length === 0) return null
  return (
    <View
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <Slider
        slidesTotalNumber={validProgressCards.length}
        slidesSpacing={spaceBetweenCards}
        slidesPerView={slidesPerView}
        loadMoreContent={handleLoadMoreContents}
        hasMore={hasMore}
        displaySliderControls={media.gtSm && !media.gtMd}
        displaySliderControlsHorizontal={shouldDisplaySliderControls}
        sliderTitle={
          <DefaultSectionTitle
            title="Produtos"
            isHoveringSection={isHovered}
            onButtonPress={handleTitleButtonPress}
            showButton={shouldDisplaySeeAllButton ?? false}
          />
        }
      >
        {validProgressCards.map((product: any) => {
          return (
            <ProductCard
              key={product.id}
              hasAuxiliarTitle={showProductsName}
              isHorizontal={isHorizontal}
              data={{
                id: product.course.id.toString(),
                enrollmentId,
                courseId: product.course.id.toString(),
                title: product.course.name,
                logo: product.course.imageUrl,
                progress: product.progress,
                totalLessons: product.total,
                completedLessons: product.completed,
                kind: product.course.kind,
                courseContentId:
                  product.course?.courseModules?.nodes?.[0]?.courseContents
                    ?.nodes?.[0]?.id ?? null,
                firstLesson:
                  product.course?.courseModules?.nodes?.[0]?.courseContents
                    ?.nodes?.[0]?.lesson ?? null,
              }}
            />
          )
        })}
      </Slider>
      {openModal && (
        <EnrollmentContentsModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          lessonCards={renderModalCards}
          title={'Produtos'}
          isLoadingMoreContents={isLoadingMore}
          onEndReached={handleLoadMoreContents}
        />
      )}
    </View>
  )
}
